import { BestRadio, Collapse, Icon, TextArea } from "best-common-react";
import clsx from "clsx";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import styled from "styled-components";
import { downloadFile, getFile } from "../../../api/FileApi";
import {
    getJurisdictionStatusesByPersonnel,
    getNonSterlingFindingsFilenames,
    getPersonnelBgcFindingsReport,
    getPersonnelById,
} from "../../../api/PersonnelApi";
import { markCompleteRoster } from "../../../api/RosterApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useAlert } from "../../../contexts/AlertsContext";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { BigsPersonnel, BigsRosterStatus } from "../../../types/personnel";
import { getApiErrorMessage } from "../../../util/ApiErrorUtil";
import { buildOrgHeaderLabel } from "../../../util/ClubDisplayUtil";
import FileUtil from "../../../util/FileUtil";
import FormUtil from "../../../util/FormUtil";
import { isObjectEmpty } from "../../../util/object";
import { RequiredLabel } from "../FormElements";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";

/** Prefer ACTIVE Findings per country, else ACTIVE Review Findings — at most one POST per jurisdiction. */
function pickMarkCompleteRosterStatuses(
    rosterStatuses: BigsRosterStatus[] | undefined,
): BigsRosterStatus[] {
    if (!rosterStatuses?.length) return [];
    const active = rosterStatuses.filter(s => s.active === "ACTIVE" && s.country);
    const byCountry = new Map<string, BigsRosterStatus>();
    for (const s of active) {
        if (s.status?.status === "Findings") {
            byCountry.set(s.country as string, s);
        }
    }
    for (const s of active) {
        if (s.status?.status === "Review Findings" && !byCountry.has(s.country as string)) {
            byCountry.set(s.country as string, s);
        }
    }
    return Array.from(byCountry.values());
}

type ModalProps = {
    modalHeight: string;
};

const BodyContent = styled.div<ModalProps>`
    text-align: left;
    margin: 0.7rem;
    padding: 0.5rem;
    height: ${props => props.modalHeight};
    color: var(--slate);
    overflow-y: auto;
`;

const NameContent = styled.div`
    font-size: 16px;
    color: var(--slate);
`;

const OrganizationContent = styled.div`
    font-size: 12px;
    color: var(--slate);
`;

const ReportsContent = styled.div`
    border: solid 1px var(--fog);
    margin-top: 1rem;
    padding: 1rem;
    background-color: var(--cloud);
    height: 210px;
    overflow-x: hidden;
    overflow-y: scroll;
`;

const ReportItems = styled.div``;
const DownloadContent = styled.div`
    margin-top: 0.3rem;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    color: var(--blue);
`;
const CommentContent = styled.div`
    margin-top: 0.3rem;
`;

const BgcScore = styled.div`
    margin-top: 0.5rem;
`;

const StyledRadioButtonText = styled.label`
    font-family: Helvetica;
    font-weight: normal;
    font-size: 14px;
`;

const RadioButtonText = ({ className, ...props }: React.ComponentPropsWithRef<"label">) => {
    return (
        <StyledRadioButtonText
            className={clsx("d-flex align-self-center mt-1 mr-3", className)}
            {...props}
        />
    );
};

const HoverIcon = styled(Icon)`
    width: 25px;
    height: 16px;
    color: var(--blue);
    cursor: pointer;
`;

const DownloadReport = styled.div`
    color: var(--blue);
    cursor: pointer;
`;

type JurisdictionReport = {
    id: number;
    name: string;
    request: string;
    result: any;
};

const BodyCollapseWrapper = styled.div`
    margin-bottom: 1rem;

    &:last-child {
        margin-bottom: 0;
    }
`;

type ReviewFindingsModalBodyContentProps = {
    readOnly: boolean;
    jurisdiction: string;
    personnel: BigsPersonnel;
    comments: Record<string, string>;
    setComments: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    bgcScores: Record<string, string>;
    setBgcScores: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};

const ReviewFindingsModalBodyContent = ({
    readOnly,
    jurisdiction,
    personnel,
    comments,
    setComments,
    bgcScores,
    setBgcScores,
}: ReviewFindingsModalBodyContentProps) => {
    // hooks
    const [downloadFiles, setDownloadFiles] = useState([]);
    const [reports, setReports] = useState<JurisdictionReport[]>([]);

    // constants
    const bgcScore = bgcScores[jurisdiction] || "";
    const comment = comments[jurisdiction] || "";
    const formattedJurisdiction = useMemo(() => FormUtil.formatJurisdiction(jurisdiction), [
        jurisdiction,
    ]);

    // functions
    const downloadReportHandler = useCallback(
        (fileList: string[]) => {
            fileList.forEach(filename => {
                const filePromise =
                    jurisdiction === "usPuerto"
                        ? downloadFile(filename)
                        : getFile(personnel.id, filename);

                filePromise.then(data => {
                    FileUtil.openFileInNewTab(data);
                });
            });
        },
        [jurisdiction, personnel],
    );

    // effects
    useEffect(() => {
        if (jurisdiction) {
            // Handle by Sterling, so get report from Sterling
            getPersonnelBgcFindingsReport(personnel.id, jurisdiction)
                .then(bgcForm => {
                    // Check if bgcForm is null or undefined (common for completed personnel)
                    if (!bgcForm) {
                        // Try to get non-Sterling findings filenames as fallback
                        getNonSterlingFindingsFilenames(personnel.id, jurisdiction)
                            .then(files => {
                                setDownloadFiles(files || []);
                            })
                            .catch(() => {
                                setDownloadFiles([]);
                            });
                        setReports([]);
                        return;
                    }

                    const reports: JurisdictionReport[] = [];
                    let index = 1;

                    // Check if formData exists before iterating
                    if (bgcForm.formData) {
                        for (const reportCategory in bgcForm.formData) {
                            const result = bgcForm.formData[reportCategory].result;
                            if (Object.keys(result).length === 0) {
                                continue;
                            }

                            reports.push({
                                id: index++,
                                name: reportCategory,
                                request: bgcForm.formData[reportCategory].request,
                                result: result,
                            });
                        }
                    }

                    setReports(reports);

                    // Check if filename exists before setting download files
                    if (bgcForm.filename) {
                        setDownloadFiles([bgcForm.filename]);
                    } else {
                        // Try to get non-Sterling findings filenames as fallback
                        getNonSterlingFindingsFilenames(personnel.id, jurisdiction)
                            .then(files => {
                                setDownloadFiles(files || []);
                            })
                            .catch(() => {
                                setDownloadFiles([]);
                            });
                    }
                })
                .catch(() => {
                    // On error, try to get non-Sterling findings filenames as fallback
                    getNonSterlingFindingsFilenames(personnel.id, jurisdiction)
                        .then(files => {
                            setDownloadFiles(files || []);
                        })
                        .catch(() => {
                            setDownloadFiles([]);
                        });
                    setReports([]);
                });
        } else {
            // Non-sterling handled jurisdiction, get filenames from Admin uploaded files.
            getNonSterlingFindingsFilenames(personnel.id, jurisdiction)
                .then(files => {
                    setDownloadFiles(files || []);
                })
                .catch(() => {
                    setDownloadFiles([]);
                });
        }
    }, [jurisdiction, personnel]);

    useEffect(() => {
        //When readOnly, the comment is actually stored in status "Findings Complete"
        //Need to get comment from there.
        if (readOnly) {
            const findingCompletedStatus = personnel?.roster?.statuses?.find(
                s =>
                    s.country === jurisdiction &&
                    s.status.status.toUpperCase() === "FINDINGS COMPLETE",
            );
            if (findingCompletedStatus) {
                setComments(prev => ({ ...prev, [jurisdiction]: findingCompletedStatus.comment }));
            }
        }
    }, [jurisdiction, personnel, readOnly, setComments]);

    useEffect(() => {
        //Once Marked as Complete - Use Statuses to Determine Setting of Pass/Fail
        const passFailScore = personnel?.roster?.statuses.find(
            s => s.status.status.includes("Fail") || s.status.status.includes("Pass"),
        );

        if (readOnly && passFailScore) {
            if (passFailScore.status?.status === "BGC score is Fail") {
                setBgcScores(prev => ({ ...prev, [jurisdiction]: "FAIL" }));
            } else {
                setBgcScores(prev => ({ ...prev, [jurisdiction]: "PASS" }));
            }
        }
    }, [jurisdiction, personnel, readOnly, setBgcScores]);

    return (
        <Collapse startsOpen title={formattedJurisdiction} styles={{ borderColor: "var(--fog)" }}>
            <ReportsContent>
                {reports.map(report => (
                    <div key={report.id}>
                        <div>
                            <span>{report.id}.)</span>
                            <span className="pl-2 text-dark font-weight-bold">{report.name}</span>
                        </div>
                        <ReportItems>
                            {Object.keys(report.result).map((keyName, idx) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Row key={idx}>
                                    <Col xs={1} />
                                    <Col xs={2}>{keyName}</Col>
                                    <Col>{report.result[keyName]}</Col>
                                </Row>
                            ))}
                        </ReportItems>
                    </div>
                ))}
            </ReportsContent>
            <DownloadContent>
                <HoverIcon
                    iconName="fa-download"
                    onClick={() => {
                        downloadReportHandler(downloadFiles);
                    }}
                />
                <DownloadReport
                    onClick={() => {
                        downloadReportHandler(downloadFiles);
                    }}
                >
                    Download Report
                </DownloadReport>
            </DownloadContent>
            <CommentContent>
                <RequiredLabel text="Comments" required={!readOnly} />
                <TextArea
                    disabled={readOnly}
                    placeholder="enter comments..."
                    value={comment}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const value = (e.target || e.currentTarget).value;
                        setComments(prev => ({
                            ...prev,
                            [jurisdiction]: value,
                        }));
                    }}
                    style={{
                        resize: "none",
                        fontSize: "14px",
                        color: "var(--black)",
                        fontWeight: "300",
                        borderColor: "var(--fog)",
                    }}
                />
            </CommentContent>
            {(personnel.personnelType.type === PersonnelTypes.VMS ||
                personnel.personnelType.type === PersonnelTypes.UMPIRE) && (
                <BgcScore>
                    <RequiredLabel text="BGC Score" required />
                    <div className="d-flex align-items-center">
                        <BestRadio
                            id="radioPass"
                            onChange={() => {
                                setBgcScores(prev => ({ ...prev, [jurisdiction]: "PASS" }));
                            }}
                            checked={bgcScore.toUpperCase() === "PASS"}
                            disabled={readOnly}
                        />
                        <RadioButtonText htmlFor="radioPass">Pass</RadioButtonText>
                        <BestRadio
                            id="radioFail"
                            onChange={() => {
                                setBgcScores(prev => ({ ...prev, [jurisdiction]: "FAIL" }));
                            }}
                            checked={bgcScore.toUpperCase() === "FAIL"}
                            disabled={readOnly}
                        />
                        <RadioButtonText htmlFor="radioFail">Fail</RadioButtonText>
                    </div>
                </BgcScore>
            )}
        </Collapse>
    );
};

type ReviewFindingsModalBodyProps = {
    personnel: BigsPersonnel;
    active: string;
    jurisdiction?: string;
    comments: Record<string, string>;
    setComments: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    bgcScores: Record<string, string>;
    setBgcScores: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};

const ReviewFindingsModalBody = ({
    personnel,
    active,
    jurisdiction: providedJurisdiction,
    comments,
    setComments,
    bgcScores,
    setBgcScores,
}: ReviewFindingsModalBodyProps) => {
    // hooks
    const [jurisdictions, setJurisdictions] = useState<string[]>([]);

    // constants
    const readOnly = !!(active && active.toUpperCase() === "INACTIVE");

    // effects
    // get jurisdictions
    useEffect(() => {
        if (personnel.id <= 0) {
            return;
        }

        // If a specific jurisdiction is provided (e.g., from activity history), use it directly
        // Check that it's not null, undefined, or empty string
        if (providedJurisdiction && providedJurisdiction.trim() !== "") {
            setJurisdictions([providedJurisdiction]);
            return;
        }

        // Otherwise, get all jurisdictions that are currently in "Review Findings" status
        getJurisdictionStatusesByPersonnel([personnel]).then(([response]) => {
            if (!response) {
                return;
            }

            // set all jurisdictions that are in "Review Findings" status
            setJurisdictions(
                response.jurisdictionStatusList
                    .filter(juris => juris.status === "Review Findings")
                    .map(juris => juris.jurisdiction),
            );
        });
    }, [personnel, providedJurisdiction]);

    return (
        <BodyContent modalHeight="450px">
            <NameContent>
                {personnel.lastName}, {personnel.firstName} - {personnel.title}
            </NameContent>
            <OrganizationContent>{buildOrgHeaderLabel(personnel)}</OrganizationContent>
            {jurisdictions.map(juris => {
                return (
                    <BodyCollapseWrapper key={juris}>
                        <ReviewFindingsModalBodyContent
                            readOnly={readOnly}
                            jurisdiction={juris}
                            personnel={personnel}
                            comments={comments}
                            setComments={setComments}
                            bgcScores={bgcScores}
                            setBgcScores={setBgcScores}
                        />
                    </BodyCollapseWrapper>
                );
            })}
        </BodyContent>
    );
};

type ReviewFindingsModalFooterProps = {
    personnel: BigsPersonnel;
    active: string;
    comments: Record<string, string>;
    setComments: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    callback: Function;
    bgcScores: Record<string, string>;
    setBgcScores: React.Dispatch<React.SetStateAction<Record<string, string>>>;
};

const ReviewFindingsModalFooter = ({
    personnel,
    active,
    comments,
    setComments,
    bgcScores,
    callback,
}: ReviewFindingsModalFooterProps) => {
    // hooks
    const showAlert = useAlert();
    const { closeModal } = useGlobalModal();
    const [submitting, setSubmitting] = useState(false);

    // constants
    const readOnly = !!(active && active.toUpperCase() === "INACTIVE");

    const formatMarkCompleteFailureSummary = (
        failed: Array<{ jurisdiction: string; message: string }>,
    ) =>
        failed
            .map(entry => `${FormUtil.formatJurisdiction(entry.jurisdiction)}: ${entry.message}`)
            .join("; ");

    const postMark = async (p: BigsPersonnel, rows: BigsRosterStatus[]) => {
        if (!p.roster?.id || !rows.length) {
            showAlert("Mark Complete failed.", "danger");
            return;
        }
        setSubmitting(true);
        const succeeded: string[] = [];
        const failed: Array<{ jurisdiction: string; message: string }> = [];

        for (const status of rows) {
            const jurisdiction = status.country ?? "";
            try {
                const result = await markCompleteRoster(p.roster!.id, {
                    rosterStatusId: status.id,
                    comment: comments[jurisdiction] || "",
                    bgcScore: bgcScores[jurisdiction] || "",
                });
                if (result?.status === "SUCCEEDED") {
                    succeeded.push(jurisdiction);
                } else {
                    failed.push({
                        jurisdiction,
                        message:
                            typeof result?.message === "string" && result.message.trim()
                                ? result.message
                                : "Mark Complete failed.",
                    });
                }
            } catch (error) {
                failed.push({
                    jurisdiction,
                    message: getApiErrorMessage(error, "Mark Complete failed."),
                });
            }
        }

        if (failed.length === 0) {
            showAlert("Complete");
        } else if (succeeded.length === 0) {
            showAlert(formatMarkCompleteFailureSummary(failed), "danger");
        } else {
            showAlert(
                `${succeeded.length} succeeded, ${
                    failed.length
                } failed. ${formatMarkCompleteFailureSummary(failed)}`,
                "danger",
            );
        }

        if (failed.length === 0 && callback && typeof callback === "function") {
            callback();
        } else if (succeeded.length > 0 && callback && typeof callback === "function") {
            callback();
        }
        if (failed.length === 0 || succeeded.length > 0) {
            setComments({});
            closeModal();
        }
        setSubmitting(false);
    };

    // functions
    const markComplete = () => {
        if (submitting) {
            return;
        }
        const statuses = pickMarkCompleteRosterStatuses(personnel.roster?.statuses);

        if (personnel.roster?.id && statuses.length) {
            void postMark(personnel, statuses);
            return;
        }

        if (personnel.id > 0) {
            getPersonnelById(String(personnel.id))
                .then((refreshed: BigsPersonnel) => {
                    const rows = pickMarkCompleteRosterStatuses(refreshed.roster?.statuses);
                    void postMark(refreshed, rows);
                })
                .catch(error =>
                    showAlert(getApiErrorMessage(error, "Mark Complete failed."), "danger"),
                );
            return;
        }

        showAlert("Mark Complete failed.", "danger");
    };

    return (
        <div className="d-flex">
            {!readOnly && (
                <PrimaryButtonStyled
                    width={142}
                    height={26}
                    disabled={
                        submitting ||
                        (!isObjectEmpty(comments) &&
                            Object.values(comments).some(comment => !comment)) ||
                        ((personnel?.personnelType?.type === PersonnelTypes.VMS ||
                            personnel?.personnelType?.type === PersonnelTypes.UMPIRE) &&
                            !isObjectEmpty(bgcScores) &&
                            Object.values(bgcScores).some(bgcScore => !bgcScore))
                    }
                    onClick={event => {
                        event.stopPropagation();
                        markComplete();
                    }}
                >
                    {submitting ? "Marking…" : "Mark Complete"}
                </PrimaryButtonStyled>
            )}

            <div className="pl-2">
                <OutlinedButtonStyled
                    width={142}
                    height={26}
                    disabled={false}
                    onClick={event => {
                        event.stopPropagation();
                        closeModal();
                    }}
                >
                    {readOnly ? "Close" : "Cancel"}
                </OutlinedButtonStyled>
            </div>
        </div>
    );
};

ReviewFindingsModalBody.displayName = "BodyContent";
ReviewFindingsModalFooter.displayName = "FooterContent";

export { ReviewFindingsModalBody, ReviewFindingsModalFooter };
